import { Check, ChevronDown } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { focusOpenedFrom, useOpenedBy } from "../hooks/useOpenedBy";
import { agentActions, type AgentState } from "../store/agentSlice";
import {
  agentUsageQueryOptions,
  currentModel,
  saveThreadModel,
  useAgentModels,
  type AgentModel,
} from "./models";
import { useNotice } from "./notice";
import { PhoneSheet, useIsPhone } from "./PhoneSheet";
import { ModelName, ProviderMark } from "./ProviderMark";
import { UsageLine } from "./UsageLine";

type State = { agent: AgentState };

/** The model the thread answers with now; null until the catalog loads. */
export function useCurrentModel(threadId: number | null): AgentModel | null {
  const catalog = useAgentModels();
  const pick = useSelector((state: State) =>
    threadId == null ? undefined : state.agent.modelPicks[threadId]
  );
  const threadRef = useSelector((state: State) =>
    threadId == null ? undefined : state.agent.threads[threadId]?.modelRef
  );
  const profile = useSelector((state: State) =>
    threadId == null ? undefined : state.agent.threads[threadId]?.profile
  );
  return catalog ? currentModel(catalog, pick, threadRef, profile) : null;
}

/** The model the thread answers with, and the list to pick another; the pick is saved at once. */
export function ModelPill({ threadId }: { threadId: number | null }) {
  const dispatch = useDispatch();
  const catalog = useAgentModels();
  const model = useCurrentModel(threadId);
  const phone = useIsPhone();
  const { setNotice } = useNotice();
  const [sheet, setSheet] = useState(false);
  const queryClient = useQueryClient();
  // Today's use is known before the picker opens: the open picker never grows under the pointer.
  useEffect(() => {
    void queryClient.prefetchQuery(agentUsageQueryOptions);
  }, [queryClient]);
  // A close hands focus back to the pill, ringed only after keys (a pick by pointer shows no ring).
  const { openedBy, openedFrom, triggerProps } = useOpenedBy();
  if (!catalog || !model) return null;

  const pick = (modelRef: string) => {
    if (threadId == null || modelRef === model.ref) return;
    dispatch(agentActions.modelPicked({ threadId, modelRef }));
    saveThreadModel(threadId, modelRef, (failed) => {
      dispatch(agentActions.modelPickFailed({ threadId, modelRef: failed }));
      setNotice("Couldn't switch the model. Try again.");
    });
  };
  const trigger = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      // Pressed (muted fill) while its menu or sheet is open, as the mockup draws it.
      className={cn(
        "h-8 gap-1.5 px-2 text-xs font-medium text-muted-foreground data-[state=open]:bg-muted max-sm:h-10",
        sheet && "bg-muted"
      )}
      disabled={threadId == null}
      aria-label={`Model: ${model.label}`}
      onClick={phone ? () => setSheet(true) : undefined}
      {...triggerProps}
    >
      <ProviderMark provider={model.provider} />
      {model.label}
      <ChevronDown className="!size-3.5" aria-hidden />
    </Button>
  );
  const row = (option: AgentModel) => (
    <>
      <ModelName model={option} />
      {option.ref === model.ref && <Check className="ml-auto h-4 w-4 text-primary" aria-hidden />}
    </>
  );

  if (phone) {
    return (
      <>
        {trigger}
        <PhoneSheet
          open={sheet}
          onOpenChange={setSheet}
          title="Model"
          openedBy={openedBy}
          openedFrom={openedFrom}
        >
          {catalog.models.map((option) => (
            <button
              key={option.ref}
              type="button"
              aria-pressed={option.ref === model.ref}
              className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-pressed:bg-accent aria-pressed:text-accent-foreground"
              onClick={() => {
                pick(option.ref);
                setSheet(false);
              }}
            >
              {row(option)}
            </button>
          ))}
          <UsageLine className="mt-2 px-3 pb-1 pt-3" />
        </PhoneSheet>
      </>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        side="top"
        align="start"
        className="min-w-[14rem]"
        onKeyDown={() => (openedBy.current = "keyboard")}
        onCloseAutoFocus={(event) => focusOpenedFrom(event, { openedBy, openedFrom })}
      >
        {catalog.models.map((option) => (
          <DropdownMenuItem
            key={option.ref}
            role="menuitemradio"
            aria-checked={option.ref === model.ref}
            className="py-2 aria-checked:bg-accent aria-checked:text-accent-foreground"
            onSelect={() => pick(option.ref)}
          >
            {row(option)}
          </DropdownMenuItem>
        ))}
        <UsageLine className="mt-1 px-2 pb-1.5 pt-2" />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
